import styled, { createGlobalStyle, css } from "styled-components";

const hardPanel = css`
  position: relative;
  border-radius: 0;
`;

export const GlobalOverride = createGlobalStyle`
  @import url("https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;700;800;900&family=Secular+One&display=swap");

  :root {
    --poster-blood: #495a2b;
    --poster-red: #7f9650;
    --poster-black: #050505;
    --poster-ash: #f3d7de;
    --poster-acid: #2a38d7;
    --poster-ink: #f5e9ec;
    --poster-steel: #2f3b1d;
  }

  html,
  body,
  #root {
    min-height: 100%;
    height: auto;
  }

  body {
    direction: rtl;
    overflow-x: hidden;
    overflow-y: hidden;
    color: var(--poster-ink);
    background:
      radial-gradient(circle at top, rgba(255, 255, 255, 0.1), transparent 28%),
      linear-gradient(180deg, #2b0008 0%, #120003 30%, #000 100%);
    font-family: "Heebo", Arial, sans-serif;
  }

  @media (max-width: 960px) {
    body {
      overflow-y: hidden;
    }

    #root {
      overflow: hidden;
    }
  }

  #root {
    overflow: hidden;
  }
`;

export const AppShell = styled.div`
  position: relative;
  height: 100vh;
  height: 100dvh;
  overflow-x: clip;
  overflow-y: hidden;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  background: linear-gradient(
      180deg,
      rgba(127, 150, 80, 0.1),
      rgba(127, 150, 80, 0)
    ),
    linear-gradient(120deg, #090b07 0%, #12170d 28%, #2f3b1d 68%, #495a2b 100%);

  @media (max-width: 960px) {
    height: 100vh;
    height: 100dvh;
    min-height: 0;
    overflow-y: hidden;
    grid-template-rows: auto minmax(0, 1fr) auto;
  }
`;

export const Content = styled.div`
  position: relative;
  z-index: 2;
  width: min(1460px, calc(100vw - 1.5rem));
  margin: 0 auto;
  text-align: center;
`;

export const Hero = styled.header`
  position: relative;
  min-height: 0;
  padding: 0.4rem 0.9rem 0.2rem;

  @media (max-width: 960px) {
    padding: 0.55rem 0.65rem 0.3rem;
  }

  @media (max-width: 720px) {
    padding-top: 0.45rem;
  }
`;

export const HeroSky = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;

  &::before,
  &::after {
    content: "";
    position: absolute;
    ${hardPanel};
    opacity: 0.95;
  }

  &::before {
    inset: 2.8rem auto auto -5rem;
    width: min(42vw, 28rem);
    height: min(22vw, 12rem);
    background: linear-gradient(90deg, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.45));
    transform: rotate(-9deg);
  }

  &::after {
    inset: auto -3rem -1rem auto;
    width: min(34vw, 23rem);
    height: min(18vw, 10rem);
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.15),
      rgba(0, 0, 0, 0.55)
    );
    transform: rotate(11deg);
  }
`;

export const HeroLogo = styled.img`
  width: clamp(4.6rem, 7vw, 6.3rem);
  margin: 0 auto;
  display: block;

  @media (max-width: 960px) {
    width: 4.4rem;
  }
`;

export const HeroTitle = styled.h1`
  position: relative;
  margin: 0;
  width: min(100%, 24ch);
  max-width: none;
  margin-inline: auto;
  font-family: "Secular One", "Heebo", sans-serif;
  direction: rtl;
  unicode-bidi: plaintext;
  font-size: clamp(2.7rem, 5.3vw, 4.6rem);
  line-height: 0.82;
  letter-spacing: -0.06em;
  text-transform: uppercase;
  color: #fff2f4;
  text-shadow: 0.05em 0.05em 0 rgba(0, 0, 0, 0.5),
    0.1em 0.1em 0 rgba(73, 90, 43, 0.45);

  span {
    display: block;
    max-width: 31rem;
    margin-top: 0.36rem;
    margin-inline: auto;
    padding: 0.5rem 0.72rem 0.46rem;
    font-family: "Heebo", Arial, sans-serif;
    font-weight: 900;
    font-size: clamp(0.96rem, 1.12vw, 1.08rem);
    line-height: 1.16;
    letter-spacing: normal;
    text-transform: none;
    direction: rtl;
    unicode-bidi: plaintext;
    text-align: right;
    color: #fff0f2;
    text-shadow: none;
    background: rgba(0, 0, 0, 0.7);
    border-right: 6px solid var(--poster-red);
  }

  span:last-child {
    background: rgba(73, 90, 43, 0.92);
    border-right-color: #050505;
    color: #fff;
  }

  @media (max-width: 960px) {
    width: min(100%, 94vw);
    font-size: clamp(2.5rem, 10.8vw, 4rem);
    line-height: 0.92;

    span {
      width: 100%;
      max-width: 100%;
      margin-top: 0.4rem;
      padding: 0.48rem 0.6rem 0.44rem;
      font-size: 0.84rem;
      line-height: 1.18;
      text-align: right;
    }
  }

  @media (max-width: 720px) {
    width: min(100%, 94vw);

    span {
      max-width: none;
    }
  }
`;

export const HeroActions = styled.div`
  margin-top: 0.5rem;
  display: none;
  justify-content: center;

  a {
    --cta-font-family: "Heebo", Arial, sans-serif;
    --cta-font-size: 1rem;
    --cta-font-weight: 900;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 11rem;
    padding: 0.64rem 1.1rem 0.58rem;
    background: var(--poster-acid);
    color: #fff;
    font-family: var(--cta-font-family);
    font-size: var(--cta-font-size);
    font-weight: var(--cta-font-weight);
    text-align: center;
    text-decoration: none;
    box-shadow: 0 0 0 4px #120003;
    transition: transform 160ms ease, box-shadow 160ms ease;

    &:hover {
      font-family: var(--cta-font-family);
      font-size: var(--cta-font-size);
      font-weight: var(--cta-font-weight);
      transform: translate(-2px, -2px);
      box-shadow: 0 0 0 4px #120003,
        0.35rem 0.35rem 0 0 rgba(127, 150, 80, 0.92);
    }
  }

  @media (max-width: 960px) {
    display: flex;
    margin-top: 0.5rem;

    a {
      --cta-font-size: 0.88rem;
      min-width: 9.5rem;
      padding: 0.58rem 0.92rem 0.54rem;
    }
  }
`;

export const DesktopSidebarActions = styled(HeroActions)`
  margin-top: 0;
  display: flex;
  justify-content: stretch;

  a {
    width: 100%;
    min-width: 0;
    padding: 0.86rem 1.1rem 0.8rem;
    --cta-font-size: 1.18rem;
  }

  @media (max-width: 960px) {
    display: none;
  }
`;

export const SidebarColumn = styled.div`
  direction: rtl;
  min-height: 0;
  height: 100%;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  gap: 0.75rem;

  @media (max-width: 960px) {
    display: grid;
    grid-template-rows: minmax(0, 1fr);
    height: clamp(6rem, 14vh, 7.5rem);
    min-height: 0;
  }
`;

export const MapSection = styled.main`
  width: min(1460px, calc(100vw - 1.5rem));
  margin: 0 auto;
  min-height: 0;
  height: 100%;
  padding: 0 0 0.35rem;
  display: grid;

  @media (max-width: 960px) {
    height: 100%;
    min-height: 0;
    padding: 0.35rem 0 0.3rem;
  }
`;

export const FooterCredit = styled.p`
  width: min(1460px, calc(100vw - 1.5rem));
  margin: 0 auto;
  padding: 0 0.2rem 0.45rem;
  direction: rtl;
  text-align: center;
  font-size: 0.72rem;
  line-height: 1.2;
  color: rgba(255, 240, 244, 0.72);

  @media (max-width: 960px) {
    padding-bottom: 0.35rem;
    font-size: 0.64rem;
  }
`;

export const MapLayout = styled.section`
  direction: ltr;
  display: grid;
  min-height: 0;
  height: 100%;
  gap: 0.75rem;
  grid-template-columns: minmax(0, 1.72fr) minmax(15rem, 0.45fr);
  align-items: stretch;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(0, 1fr) auto;
    height: 100%;
    min-height: 0;
    gap: 0.7rem;
  }
`;

const basePanel = css`
  ${hardPanel};
  overflow: hidden;
  border: 3px solid rgba(255, 255, 255, 0.14);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.4), 0 18px 42px rgba(0, 0, 0, 0.28);
`;

export const MapFrame = styled.div`
  ${basePanel};
  position: relative;
  min-height: 0;
  height: 100%;
  background: #180005;

  @media (max-width: 960px) {
    min-height: 34vh;
    height: min(42vh, 24rem);
  }

  .maplibregl-map {
    height: 100%;
    width: 100%;
    filter: grayscale(0.15) saturate(0.95) contrast(1.08) brightness(0.92);
  }

  .maplibregl-ctrl-top-left {
    top: 0.8rem;
    left: 0.8rem;
  }

  .maplibregl-ctrl-group {
    border-radius: 0;
    box-shadow: none;
    border: 2px solid rgba(0, 0, 0, 0.85);
    overflow: hidden;
  }

  .maplibregl-ctrl button {
    background: var(--poster-acid);
    color: #fff;
  }

  .maplibregl-ctrl button .maplibregl-ctrl-icon {
    filter: brightness(0) invert(1);
  }
`;

export const MapLabel = styled.div`
  position: absolute;
  top: 1.2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  display: grid;
  gap: 0.05rem;
  pointer-events: none;
  text-align: center;
  text-shadow: 0.04em 0.04em 0 rgba(0, 0, 0, 0.7),
    0.08em 0.08em 0 rgba(100, 0, 15, 0.45);

  strong,
  span {
    display: block;
    width: fit-content;
    margin-inline: auto;
    padding: 0.18rem 0.45rem;
    background: rgba(0, 0, 0, 0.52);
  }

  strong {
    font-family: "Secular One", "Heebo", sans-serif;
    font-size: clamp(2.2rem, 3.8vw, 4rem);
    line-height: 0.9;
    color: #fff4f6;
  }

  span {
    font-family: "Secular One", "Heebo", sans-serif;
    font-size: clamp(1.4rem, 2.2vw, 2.2rem);
    line-height: 0.94;
    color: var(--poster-acid);
  }

  @media (max-width: 960px) {
    top: 0.8rem;

    strong {
      font-size: clamp(1.55rem, 6vw, 2.4rem);
    }
  }
`;

export const Panel = styled.aside`
  ${basePanel};
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  min-height: 0;
  height: 100%;
  background: linear-gradient(
    180deg,
    rgba(127, 150, 80, 0.94),
    rgba(73, 90, 43, 0.98)
  );

  @media (max-width: 960px) {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }
`;

export const PanelSection = styled.section<{ $scrollable?: boolean }>`
  padding: 1rem 0.95rem 0.92rem;
  color: #fff6fb;

  ${(props) =>
    props.$scrollable
      ? css`
          flex: 1 1 auto;
          min-height: 0;
          overflow: auto;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
          background: rgba(0, 0, 0, 0.56);
        `
      : css`
          border-bottom: 2px solid rgba(255, 255, 255, 0.18);
        `}

  h3 {
    margin: 0;
    font-family: "Secular One", "Heebo", sans-serif;
    font-size: 1.42rem;
    line-height: 1.03;
    letter-spacing: -0.04em;
    color: #fff4f6;
  }

  @media (max-width: 960px) {
    padding: 0.78rem 0.74rem 0.68rem;

    h3 {
      font-size: 1.08rem;
      line-height: 1.06;
    }
  }
`;

export const MetaLine = styled.p`
  margin: 0.6rem 0 0.75rem;
  color: rgba(255, 240, 244, 0.9);
  font-size: 1rem;
  font-weight: 700;

  @media (max-width: 960px) {
    margin: 0.4rem 0 0.55rem;
    font-size: 0.84rem;
  }
`;

export const AudioPlayer = styled.audio`
  width: 100%;
  filter: grayscale(0.1) saturate(0.85) contrast(1.1);
`;

export const ClipList = styled.ul`
  display: grid;
  gap: 0.68rem;

  @media (max-width: 960px) {
    gap: 0.5rem;
  }
`;

export const ClipListButton = styled.button<{ $active?: boolean }>`
  width: 100%;
  padding: 0.95rem 0.92rem 0.88rem;
  text-align: right;
  color: ${(props) => (props.$active ? "#fff" : "#fff6fb")};
  background: ${(props) =>
    props.$active ? "var(--poster-acid)" : "rgba(0, 0, 0, 0.4)"};
  border-inline-end: 6px solid
    ${(props) =>
      props.$active ? "var(--poster-red)" : "rgba(255, 255, 255, 0.14)"};
  border-block: 1px solid rgba(255, 255, 255, 0.08);
  border-inline-start: 1px solid rgba(255, 255, 255, 0.08);
  transition: transform 160ms ease, background 160ms ease,
    border-color 160ms ease;

  &:hover {
    transform: translate(-2px, -2px);
    background: ${(props) =>
      props.$active ? "var(--poster-acid)" : "rgba(0, 0, 0, 0.58)"};
    border-inline-end-color: var(--poster-red);
  }

  @media (max-width: 960px) {
    padding: 0.72rem 0.72rem 0.66rem;
  }
`;

export const ClipListTitle = styled.span`
  display: block;
  font-size: 1.04rem;
  font-weight: 900;
  line-height: 1.16;

  @media (max-width: 960px) {
    font-size: 0.9rem;
  }
`;

export const ClipListMeta = styled.span`
  display: block;
  margin-top: 0.3rem;
  color: inherit;
  opacity: 0.82;
  font-size: 0.82rem;

  @media (max-width: 960px) {
    font-size: 0.72rem;
  }
`;
